import DataSourceItemUpload from '@/components/dataSource/dataSourceItemUpload';
import { useApiContext } from '@/contexts/apiContext';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LaunchIcon from '@mui/icons-material/Launch';
import { Box, Button, Chip, CircularProgress, Divider, Stack, Typography, alpha, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type Props = {
  surveyId: string;
  responseId: string;
  userResponses: Record<string, any>;
  maxHeight?: number;
};

const StepComplete = ({ surveyId, responseId, userResponses, maxHeight }: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();
  const navigate = useNavigate();
  const theme = useTheme();

  const [containerRef] = useElementSize();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [dataSource, setDataSource] = useState<any>(null);
  const [userAppInstance, setUserAppInstance] = useState<any>(null);

  useEffect(() => {
    const getSurveyPipelineItems = async () => {
      try {
        setLoading(true);
        const response = await apiContext.getUserSurveyProcessingResult(surveyId, responseId);
        if (response && response.items) {
          setDataSource(response.items.find((item: any) => item.entityType === 'dataSourceInstance'));
          setUserAppInstance(response.items.find((item: any) => item.entityType === 'userAppInstance'));
        }
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    if (surveyId && responseId) {
      getSurveyPipelineItems();
    }
  }, [surveyId, responseId]);

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  const isUserUpload = dataSource && dataSource.dataSourceDef && dataSource.dataSourceDef.id === 'userUpload';

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderLeft: `3px solid ${theme.palette.secondary.main}`,
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        p: 3,
      }}
      ref={containerRef}
    >
      {/* Scrollable content container */}
      <Box sx={{ flex: 1, overflowY: 'auto', mb: 2 }}>
        <Stack spacing={3}>
          {/* Success header */}
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckCircleOutlineIcon
              sx={{ fontSize: 24, color: theme.palette.success?.main || 'success.main', flexShrink: 0 }}
            />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Your agentic pipeline is ready!
              </Typography>
            </Box>
          </Stack>

          <Divider />

          {/* Inline upload if userUpload data source */}
          {!loading && isUserUpload && (
            <Box>
              <DataSourceItemUpload
                dataSourceInstance={dataSource}
                header="Upload your first documents"
                onCancel={async () => {}}
              />
            </Box>
          )}

          {/* Next steps summary */}
          {!isUserUpload && (
            <Stack spacing={1.5}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Next steps
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Chip
                  size="small"
                  label="1"
                  sx={{
                    bgcolor: alpha(theme.palette.secondary.main, 0.15),
                    color: 'secondary.dark',
                    fontWeight: 700,
                    minWidth: 28,
                  }}
                />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    View your results
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Open the app to see the output from your pipeline.
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Action buttons pinned at the bottom */}
      <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="flex-end" sx={{ pt: 1 }}>
        <Button
          variant="text"
          onClick={goToDashboard}
          startIcon={<DashboardIcon fontSize="small" />}
          sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
        >
          {t('Go to dashboard')}
        </Button>
        {!loading && dataSource && dataSource.dataSourceDef && (
          <Button
            variant="outlined"
            color="secondary"
            href={`/dataSourceInstance/${dataSource.dataSourceDef.id}/${dataSource.id}`}
            target="_blank"
            startIcon={<LaunchIcon fontSize="small" />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '10px',
              px: 2.5,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            {t('Open data source')}
          </Button>
        )}
        {userAppInstance && (
          <Button
            variant="contained"
            color="secondary"
            href={`/userAppInstance/${userAppInstance.userAppDef.id}/${userAppInstance.id}`}
            target="_blank"
            startIcon={<LaunchIcon fontSize="small" />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '10px',
              px: 2.5,
              color: 'text.primary',
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            {t('Open app')}
          </Button>
        )}
        {loading && <CircularProgress size={24} color="secondary" />}
      </Stack>
    </Box>
  );
};

export default StepComplete;
